import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Flex, Spinner, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { formatExtractElapsed } from '../formatAccountingSnapshot';

function isActiveDriveExtractStatus(status) {
  return status === 'running' || status === 'pending';
}

function alertStatus(status) {
  switch (status) {
    case 'complete':
      return 'success';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'warning';
    default:
      return 'info';
  }
}

function statusTitle(status) {
  switch (status) {
    case 'running':
    case 'pending':
      return 'Re-extract in progress';
    case 'complete':
      return 'Re-extract complete';
    case 'failed':
      return 'Re-extract failed';
    case 'skipped':
      return 'Re-extract skipped';
    default:
      return 'Re-extract status';
  }
}

export function DriveExtractStatusBanner({ driveExtractStatus }) {
  const [elapsed, setElapsed] = useState(null);

  const status = driveExtractStatus?.status;
  const active = isActiveDriveExtractStatus(status);

  useEffect(() => {
    if (!active || !driveExtractStatus?.startedAt) {
      setElapsed(null);
      return undefined;
    }
    const tick = () => setElapsed(formatExtractElapsed(driveExtractStatus.startedAt));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [active, driveExtractStatus?.startedAt]);

  if (!driveExtractStatus?.status) {
    return null;
  }

  const finishedAt = driveExtractStatus.finishedAt;
  const resultParts = [];
  if (driveExtractStatus.accountingExtracted) {
    resultParts.push('accounting saved');
  }
  if (driveExtractStatus.cohortExtracted) {
    resultParts.push('cohort saved');
  }

  return (
    <Alert status={active ? 'info' : alertStatus(status)} variant="left-accent" mb={4}>
      {active ? <Spinner size="sm" mr={3} color="purple.500" /> : <AlertIcon />}
      <Box flex={1}>
        <AlertTitle fontSize="sm">{statusTitle(status)}</AlertTitle>
        <AlertDescription fontSize="sm">
          <Text>{driveExtractStatus.stageMessage || 'Processing Drive folder…'}</Text>
          <Flex mt={1} gap={2} wrap="wrap" fontSize="xs" color="gray.600">
            {driveExtractStatus.triggeredBy ? (
              <Text>Triggered by {driveExtractStatus.triggeredBy}</Text>
            ) : null}
            {active && elapsed ? <Text>Running for {elapsed}</Text> : null}
            {!active && finishedAt ? (
              <Text>Finished {new Date(finishedAt).toLocaleString()}</Text>
            ) : null}
            {!active && resultParts.length > 0 ? (
              <Text>{resultParts.join(' · ')}</Text>
            ) : null}
          </Flex>
          {driveExtractStatus.error ? (
            <Text mt={2} fontSize="sm" color="red.600">
              {driveExtractStatus.error}
            </Text>
          ) : null}
        </AlertDescription>
      </Box>
    </Alert>
  );
}

DriveExtractStatusBanner.propTypes = {
  driveExtractStatus: PropTypes.shape({
    status: PropTypes.string,
    stage: PropTypes.string,
    stageMessage: PropTypes.string,
    triggeredBy: PropTypes.string,
    startedAt: PropTypes.string,
    finishedAt: PropTypes.string,
    error: PropTypes.string,
    accountingExtracted: PropTypes.bool,
    cohortExtracted: PropTypes.bool,
  }),
};

export { isActiveDriveExtractStatus };
